// src/frontend/src/api/cache.ts

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

const cache = new Map<string, CacheEntry<unknown>>();
const DEFAULT_TTL_MS = 5 * 60 * 1000; // 5 minutes

export interface CachedFetchOptions {
  force?: boolean;
  ttlMs?: number;
}

/**
 * Fetch with in-memory caching.
 *
 * Reference data (waste types, annual calendar, special events) changes very rarely.
 * Caching in memory eliminates the full-screen loading spinner when residents navigate
 * between tabs (Home <-> A-Z Guide <-> Calendar <-> Locations), rendering immediately.
 *
 * When an error occurs during fetch, the error is NOT cached and is thrown immediately,
 * preserving the invariant that API calls throw on failure.
 *
 * Callers using `useRetry()` can pass `{ force: true }` to bypass and refresh the cache.
 */
export async function cachedFetch<T>(
  key: string,
  fetcher: () => Promise<T>,
  options: CachedFetchOptions = {}
): Promise<T> {
  const { force = false, ttlMs = DEFAULT_TTL_MS } = options;
  const now = Date.now();

  if (!force) {
    const entry = cache.get(key) as CacheEntry<T> | undefined;
    if (entry && now - entry.timestamp < ttlMs) {
      return entry.data;
    }
  }

  const data = await fetcher();
  cache.set(key, { data, timestamp: now });
  return data;
}

/** Invalidate a specific cache key or all keys (useful for retries or testing). */
export function invalidateCache(key?: string): void {
  if (key) {
    cache.delete(key);
  } else {
    cache.clear();
  }
}
